import React, { useState, useEffect, useCallback } from 'react';

const Rocketize = ({ active = true }) => {
  const [konamiProgress, setKonamiProgress] = useState(0);
  const [isActive, setIsActive] = useState(false);

  // Konami Code: ↑ ↑ ↓ ↓ ← → ← → B A
  const konamiCode = [38, 38, 40, 40, 37, 39, 37, 39, 66, 65];

  const triggerRocket = useCallback(() => {
    if (isActive) return;

    setIsActive(true);

    const rocket = document.createElement('div');
    rocket.innerHTML = '🚀';
    rocket.style.cssText = `
      position: fixed;
      bottom: -50px;
      left: -50px;
      width: 80px;
      height: 80px;
      font-size: 60px;
      z-index: 99999;
      pointer-events: none;
      transition: all 2s ease-in-out;
      display: flex;
      align-items: center;
      justify-content: center;
      transform: rotate(45deg);
    `;

    document.body.appendChild(rocket);

    // Animate across screen from bottom left to top right
    setTimeout(() => {
      rocket.style.transform = 'translateX(calc(100vw + 100px)) translateY(calc(-100vh - 100px)) rotate(45deg)';
    }, 0);

    // Play psychedelic alien UFO sounds
    try {
      const audioContext = new AudioContext();

      // Create multiple oscillators for a complex UFO sound
      const osc1 = audioContext.createOscillator();
      const osc2 = audioContext.createOscillator();
      const osc3 = audioContext.createOscillator();
      const gain1 = audioContext.createGain();
      const gain2 = audioContext.createGain();
      const gain3 = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();

      // Connect oscillators through filter
      osc1.connect(gain1);
      osc2.connect(gain2);
      osc3.connect(gain3);
      gain1.connect(filter);
      gain2.connect(filter);
      gain3.connect(filter);
      filter.connect(audioContext.destination);

      // UFO swoosh sound with frequency modulation
      osc1.frequency.setValueAtTime(440, audioContext.currentTime);
      osc1.frequency.exponentialRampToValueAtTime(880, audioContext.currentTime + 0.5);
      osc1.frequency.exponentialRampToValueAtTime(220, audioContext.currentTime + 1.5);
      osc1.type = 'sine';

      // Alien warble
      osc2.frequency.setValueAtTime(660, audioContext.currentTime);
      osc2.frequency.linearRampToValueAtTime(330, audioContext.currentTime + 0.3);
      osc2.frequency.linearRampToValueAtTime(990, audioContext.currentTime + 0.8);
      osc2.frequency.linearRampToValueAtTime(165, audioContext.currentTime + 1.5);
      osc2.type = 'triangle';

      // Psychedelic modulation
      osc3.frequency.setValueAtTime(110, audioContext.currentTime);
      osc3.frequency.exponentialRampToValueAtTime(55, audioContext.currentTime + 2);
      osc3.type = 'sawtooth';

      // Filter sweep for psychedelic effect
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(2000, audioContext.currentTime);
      filter.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 1);
      filter.frequency.exponentialRampToValueAtTime(8000, audioContext.currentTime + 2);

      // Gain envelopes
      gain1.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.1, audioContext.currentTime + 0.5);
      gain1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 2);

      gain2.gain.setValueAtTime(0.2, audioContext.currentTime);
      gain2.gain.linearRampToValueAtTime(0.4, audioContext.currentTime + 0.8);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 2);

      gain3.gain.setValueAtTime(0.1, audioContext.currentTime);
      gain3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 2);

      // Start and stop all oscillators
      [osc1, osc2, osc3].forEach((osc) => {
        osc.start(audioContext.currentTime);
        osc.stop(audioContext.currentTime + 2);
      });
    } catch (error) {
      // Ignore audio errors
    }

    // Remove after animation
    setTimeout(() => {
      if (rocket.parentNode) {
        rocket.parentNode.removeChild(rocket);
      }
      setIsActive(false);
    }, 2500);
  }, [isActive]);

  useEffect(() => {
    if (!active) return;

    const handleKeyDown = (event) => {
      const expectedKey = konamiCode[konamiProgress];

      if (event.keyCode === expectedKey) {
        const newProgress = konamiProgress + 1;
        setKonamiProgress(newProgress);

        if (newProgress === konamiCode.length) {
          triggerRocket();
          setKonamiProgress(0);
        }
      } else {
        setKonamiProgress(0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active, konamiProgress, triggerRocket]);

  return null;
};

export default Rocketize;
